import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {$getRoot, $isElementNode, NodeKey} from 'lexical';
import {useCallback, useEffect} from 'react';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {$createWordCountDecoratorNode} from './WordCountDecoratorNode';

/**
 * WordCountPlugin manages word count decorator nodes for headings
 * to display live word counts of content under each heading.
 */
export const WordCountPlugin = ({isTemplateMode = false}: {isTemplateMode?: boolean}) => {
  const [editor] = useLexicalComposerContext();

  const addWordCountNodesToHeadings = useCallback(() => {
    if (isTemplateMode) {
      return;
    }

    editor.update(() => {
      const root = $getRoot();
      const processedHeadings = new Set<NodeKey>();

      // Recursively find all headings in the document
      const findHeadings = (node: any) => {
        // Only process actual heading elements with proper tags
        if (
          $isElementNode(node) &&
          (node instanceof HeadingNode || node instanceof EnhancedHeadingNode) &&
          node.getTag &&
          ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.getTag())
        ) {
          const headingId =
            node instanceof EnhancedHeadingNode ? node.getHeadingId() : node.getKey();

          const hasLimit = Boolean(
            node instanceof EnhancedHeadingNode ? node.getWordLimit() : undefined
          );

          // Skip if we've already processed this heading
          if (processedHeadings.has(headingId)) {
            return;
          }
          processedHeadings.add(headingId);

          // Check if this heading already has a word count node
          const children = node.getChildren();
          const hasWordCount = children.some(
            (child) =>
              child.getType() === 'WordCount' && (child as any).getHeadingId() === headingId
          );

          // Only add word count if the heading has text content
          const hasTextContent = node.getTextContent().trim().length > 0;

          // Add word count decorator node if not present and has text
          if (!hasWordCount && hasTextContent && hasLimit) {
            const wordCountNode = $createWordCountDecoratorNode(headingId);
            node.append(wordCountNode);
          }

          // Remove word count if heading becomes empty
          if (!hasTextContent) {
            const nodeChildren = node.getChildren();
            nodeChildren.forEach((child) => {
              if (child.getType() === 'WordCount') {
                child.remove();
              }
            });
          }
        }

        // Recursively check children
        if (node.getChildren) {
          node.getChildren().forEach(findHeadings);
        }
      };

      // Start from root and search recursively
      findHeadings(root);
    });
  }, [editor, isTemplateMode]);

  useEffect(() => {
    // Initial setup
    addWordCountNodesToHeadings();

    // Handle when headings are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, () => {
      addWordCountNodesToHeadings();
    });

    // Listen for editor updates - add word count nodes when needed
    const removeUpdateListener = editor.registerUpdateListener(({dirtyElements, dirtyLeaves}) => {
      // Add word count nodes when there are structural changes
      const hasStructuralChanges = dirtyElements.size > 0 || dirtyLeaves.size > 0;

      if (hasStructuralChanges) {
        addWordCountNodesToHeadings();
      }
    });

    return () => {
      removeNodeTransform();
      removeUpdateListener();
    };
  }, [editor, addWordCountNodesToHeadings]);

  return null; // This plugin doesn't render anything
};
